<script setup>
import { useTabStore } from "@/stores/tab";
import { useRouter } from "vue-router";
import { watch } from "vue";
import { useLayoutStore } from "@/stores/layout";

const tabStore = useTabStore();
const router = useRouter();
const layoutStore = useLayoutStore();

watch(
  () => tabStore.activeTabIndex,
  async (newIndex) => {
    const selectedTab = tabStore.tabs[newIndex];
    if (selectedTab && router.currentRoute.value.path !== selectedTab.path) {
      try {
        await router.push({
          path: selectedTab.path,
          query: selectedTab.query || {},
          params: selectedTab.params || {},
          state: { layoutMode: layoutStore.layoutMode }, // 라우터 상태에 layoutMode 추가
        });
        // 레이아웃 모드 유지

        //layoutStore.setLayoutMode(layoutStore.layoutMode);
      } catch (error) {
        console.error("라우팅 에러:", error);
      }
    }
  },
  { immediate: true }
);

const closeTab = async (e, index) => {
  e.stopPropagation(); // 이벤트 전파 중지

  if (tabStore.tabs.length <= 1) return; // 마지막 탭은 닫지 않음

  const removedTab = tabStore.tabs[index];
  const isActiveTab = index === tabStore.activeTabIndex;

  tabStore.removeTab(index);

  if (isActiveTab && tabStore.tabs.length > 0) {
    const nextTab = tabStore.tabs[tabStore.activeTabIndex];
    try {
      await router.push({
        path: nextTab.path,
        query: nextTab.query || {},
        params: nextTab.params || {},
      });
    } catch (error) {
      console.error("탭 닫기 후 라우팅 에러:", error);
    }
  }
};

const onTabClick = async (index) => {
  if (index === tabStore.activeTabIndex) return;

  const selectedTab = tabStore.tabs[index];
  if (selectedTab && router.currentRoute.value.path !== selectedTab.path) {
    tabStore.activeTabIndex = index;
  }
};
</script>

<template>
  <div class="mdi-wrapper">
    <!-- 컨텐츠 영역 -->
    <div class="mdi-container">
      <!-- Breadcrumb 및 기타 헤더 컨텐츠를 위한 영역 -->
      <div class="mdi-header" v-if="layoutStore.showBreadcrumb">
        <slot name="header"></slot>
      </div>

      <!-- 탭뷰 사용 시 -->
      <TabView
        v-if="layoutStore.useTabView"
        v-model:activeIndex="tabStore.activeTabIndex"
        :scrollable="true"
      >
        <TabPanel
          v-for="(tab, index) in tabStore.tabs"
          :key="tab.id"
          @click="onTabClick(index)"
        >
          <template #header>
            <div class="flex align-items-center">
              <span>{{ tab.title }}</span>
              <button
                class="close-btn"
                @click="(e) => closeTab(e, index)"
                v-if="tabStore.tabs.length > 1"
              >
                <i class="pi pi-times"></i>
              </button>
            </div>
          </template>
          <router-view v-if="tab.path === $route.path" />
        </TabPanel>
      </TabView>
      <!-- 탭뷰 미사용 시 -->
      <router-view v-else />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.mdi-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1; // height: calc() 대신 flex: 1 사용
  min-height: 0; // flex child가 제대로 작동하게 함
  //overflow: hidden;
}

.mdi-header {
  background-color: transparent;
  position: relative;
  width: 100%;
  z-index: 1;
  flex-shrink: 0; // 헤더 크기 고정
}

.mdi-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; // 중요: 내부 스크롤을 위해 필요
  overflow: auto; // auto 대신 hidden 사용

  :deep(.p-tabview) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0; // 추가

    .p-tabview-nav-container {
      flex-shrink: 0;
      background-color: var(--surface-card);
      border-bottom: 1px solid var(--surface-border); /* 아래 경계선 추가 */
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* 그림자 추가 */
    }

    .p-tabview-nav {
      border: none;
      background: var(--surface-ground);
      padding: 0 1rem;
      display: flex;
      gap: 4px; /* 탭 간 간격 설정 */

      .p-tabview-header {
        // 이 부분이 각 탭 아이템
        margin-right: 4px;
        position: relative;

        &:not(:first-child) {
          box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1); /* 탭 간 그림자 추가 */
        }

        &:not(:first-child)::before {
          content: "";
          position: absolute;
          left: -2px;
          top: 25%;
          height: 50%;
          width: 1px;
          background: var(--surface-border);
        }

        .p-tabview-nav-link {
          background: var(--surface-card);
          border: 1px solid var(--surface-border);
          border-bottom: none;
          border-radius: 6px 6px 0 0;
          transition: all 0.2s;
          padding: 1rem !important;
          position: relative;

          &:not(.p-disabled):focus {
            box-shadow: inset 0 0 0 1px var(--primary-color);
          }

          &:hover {
            background: var(--surface-hover);
          }

          &.p-highlight {
            background: white;
            border-color: var(--primary-color);
            color: var(--primary-color);
            font-weight: 600;
            box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.1);
            position: relative;
            z-index: 1;

            &::after {
              content: "";
              position: absolute;
              bottom: -1px;
              left: 0;
              right: 0;
              height: 2px;
              background: white;
            }
          }
        }
      }
    }

    .p-tabview-panels {
      flex: 1;
      //overflow: auto;
      //min-height: 0; // 추가
      padding: 1.25rem;
      border: 1px solid var(--surface-border);
      border-radius: 0 6px 6px 6px;
      background: white;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .p-tabview-panel {
      height: 100%;
      overflow: auto; // 각 패널도 스크롤 허용
    }
  }

  // 스크롤 버튼 스타일
  :deep(.p-tabview-nav-prev),
  :deep(.p-tabview-nav-next) {
    padding: 0.5rem;
    border-radius: 50%;
    background: var(--surface-ground);
    border: 1px solid var(--surface-border);
    color: var(--text-color);
    transition: all 0.2s;

    &:enabled:hover {
      background: var(--surface-hover);
      color: var(--text-color);
    }

    &:focus {
      box-shadow: 0 0 0 2px var(--primary-color-light);
    }
  }

  :deep(.p-tabview-tablist) {
    padding-top: 0rem;
    padding-bottom: 0rem;
    padding-left: 1rem;
  }

  // 기존의 닫기 버튼 스타일 유지
  .close-btn {
    margin-left: 0.5rem;
    width: 1.2rem;
    height: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
    color: var(--text-color-secondary);
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--surface-hover);
      color: var(--text-color);
    }

    i {
      font-size: 0.75rem;
    }
  }
}
</style>
